import { useField } from 'formik';
import { RefObject, Suspense } from 'react';
import { graphql } from 'react-relay';

import useSearchOverlayQueryLoader from '../../../hooks/useSearchOverlayQueryLoader';
import { BookPublisherSearchOverlayField_bookPublishersQuery } from '../../../relay/__generated__/BookPublisherSearchOverlayField_bookPublishersQuery.graphql';
import { isNullable } from '../../../utils/is';
import ItemList from '../../core/ItemList';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import ScrollContainer from '../../core/ScrollContainer';
import SearchOverlayField, { SearchOverlayFieldProps } from '../../core/SearchOverlayField';
import Spinner from '../../core/Spinner';
import Text from '../../core/Text';
import View from '../../core/View';

const bookPublishersForBookPublisherSearchOverlayField = graphql`
  query BookPublisherSearchOverlayField_bookPublishersQuery($filters: BookPublishersFilter) {
    bookPublishers(filters: $filters)
  }
`;

type Props = {} & Omit<SearchOverlayFieldProps<{ id: string }>, 'renderItemList'>;

const BookPublisherSearchOverlayField = ({ name = '', value: propValue, ...props }: Props) => {
  const [{ value: baseValue }] = useField<string>({ name });
  const value = !isNullable(propValue) ? propValue : baseValue;

  const [searchOverlayFieldProps, queryReference] =
    useSearchOverlayQueryLoader<BookPublisherSearchOverlayField_bookPublishersQuery>(
      bookPublishersForBookPublisherSearchOverlayField,
    );

  return (
    <SearchOverlayField
      value={value}
      name={name}
      renderItemList={({ controllingElementRef, containerRef, handleSelect }, itemProps) =>
        queryReference ? (
          <Suspense fallback={<Spinner sx={{ paddingY: 3 }} />}>
            <PreloadedQueryRenderer
              query={bookPublishersForBookPublisherSearchOverlayField}
              queryReference={queryReference}
            >
              {({ bookPublishers }) => {
                const items = bookPublishers.map((bookPublisher) => ({ id: bookPublisher, title: bookPublisher }));
                const searchValue = controllingElementRef.current?.value;
                return (
                  <ScrollContainer ref={containerRef as RefObject<HTMLDivElement>}>
                    <ItemList
                      // 목록에 없는 출판사면 입력한 그대로 새로 넣을 수 있게 맨 위에 둔다.
                      items={
                        searchValue && !bookPublishers.includes(searchValue)
                          ? [{ id: searchValue, title: `"${searchValue}"으로 입력` }, ...items]
                          : items
                      }
                      renderItem={({ title }) => <Text sx={{ fontSize: 1 }}>{title}</Text>}
                      renderItemWrapper={(children, { id }, i) => (
                        <View
                          {...itemProps}
                          key={`${id}-${i}`}
                          onClick={(e) => handleSelect(e, { id })}
                          onKeyDown={(e) => handleSelect(e, { id })}
                        >
                          {children}
                        </View>
                      )}
                    />
                  </ScrollContainer>
                );
              }}
            </PreloadedQueryRenderer>
          </Suspense>
        ) : null
      }
      {...searchOverlayFieldProps}
      {...props}
    />
  );
};

export default BookPublisherSearchOverlayField;
